import { Link } from 'react-router-dom';
import { isProduction } from '@/lib/api';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="px-6 lg:px-16 py-6 flex justify-between items-center bg-charcoal">
        <Link to="/" className="font-display text-2xl font-bold text-warm-white tracking-tight">
          venndela
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/login" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
            Log In
          </Link>
          <Link to={isProduction() ? '/coming-soon' : '/register'} className="btn-primary px-6 py-2 text-sm">
            Get Started
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Terms of Service</h1>
        <p className="text-charcoal/60 mb-12">Last updated: January 2025 | Version 2025.01.1</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">1. Acceptance of Terms</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              By accessing or using Venndela ("Service"), you agree to be bound by these Terms of Service ("Terms").
              If you disagree with any part of these terms, you may not access the Service.
            </p>
            <p className="text-charcoal/80 leading-relaxed">
              These Terms apply to all visitors, users, and others who access or use the Service, including
              Organizations running affiliate programs and Affiliates promoting products through the platform.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">2. Description of Service</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Venndela is a business-to-business software-as-a-service platform that enables organizations to create,
              manage, and operate affiliate marketing programs. The Service facilitates:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2 mb-4">
              <li>Creation and management of affiliate programs</li>
              <li>Tracking of referrals, clicks, and conversions</li>
              <li>Commission calculation and payout processing</li>
              <li>Affiliate recruitment and management</li>
              <li>Analytics and reporting</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">3. Account Registration</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">4. Acceptable Use</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              You agree to use the Service only for lawful purposes and in accordance with our{' '}
              <Link to="/legal/acceptable-use" className="text-rust hover:text-rust-light underline">
                Acceptable Use Policy
              </Link>. You shall not:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Use the Service for any prohibited business category</li>
              <li>Engage in fraudulent, deceptive, or misleading practices</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any systems</li>
              <li>Use the Service to promote illegal products or services</li>
              <li>Harvest or collect user information without consent</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">5. Prohibited Business Categories</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              The following business categories are strictly prohibited from using Venndela:
            </p>
            <div className="bg-rust/10 border border-rust/20 rounded-lg p-6 mb-4">
              <h3 className="font-display font-bold text-charcoal mb-3">Completely Prohibited:</h3>
              <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm">
                <li>Adult content, pornography, or sexually explicit material</li>
                <li>Gambling, casinos, sports betting, or lotteries</li>
                <li>Illegal drugs, controlled substances, or drug paraphernalia</li>
                <li>Cannabis, marijuana, or THC products</li>
                <li>Weapons, firearms, ammunition, or explosives</li>
                <li>Counterfeit goods or intellectual property infringement</li>
                <li>Multi-level marketing (MLM) or pyramid schemes</li>
                <li>Cryptocurrency mining or staking operations</li>
                <li>"Get rich quick" schemes or deceptive business opportunities</li>
                <li>Unlicensed financial services or money transmission</li>
                <li>Debt collection agencies</li>
                <li>Fake reviews, testimonials, or reputation manipulation</li>
              </ul>
            </div>
            <p className="text-charcoal/80 leading-relaxed">
              Violation of these prohibitions will result in immediate account termination without refund.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">6. Affiliate Marketing Structure</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Venndela supports single-tier affiliate marketing programs only. This means:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2 mb-4">
              <li>Affiliates earn commissions only on sales they directly generate</li>
              <li>There are no commissions for recruiting other affiliates</li>
              <li>No "downline" or "upline" commission structures are permitted</li>
              <li>Multi-tier or network marketing structures are prohibited</li>
            </ul>
            <p className="text-charcoal/80 leading-relaxed">
              Organizations may not use Venndela to operate or facilitate multi-level marketing programs.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">7. Payment Terms</h2>
            <h3 className="font-display text-xl font-bold text-charcoal mb-3">7.1 Subscription Fees</h3>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Organizations pay subscription fees according to their selected plan. Fees are billed in advance
              on a monthly or annual basis and are non-refundable except as required by law.
            </p>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">7.2 Platform Fees</h3>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Venndela charges a platform fee on commissions processed, which varies by subscription tier.
              This fee is deducted before affiliate payouts.
            </p>

            <h3 className="font-display text-xl font-bold text-charcoal mb-3">7.3 Affiliate Payouts</h3>
            <p className="text-charcoal/80 leading-relaxed">
              Affiliate commissions are subject to a hold period (default 14 days) before becoming payable.
              Minimum payout thresholds apply (default $50). Payouts are processed via Stripe Connect.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">8. Income Disclaimer</h2>
            <div className="bg-tan/30 border border-tan rounded-lg p-6">
              <p className="text-charcoal/80 leading-relaxed mb-4">
                <strong>No Guaranteed Earnings:</strong> Venndela makes no representations or guarantees
                regarding potential earnings from affiliate marketing. Results vary based on numerous factors
                including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-charcoal/80 space-y-1 text-sm mb-4">
                <li>Product quality and market demand</li>
                <li>Marketing skills and effort</li>
                <li>Audience size and engagement</li>
                <li>Commission structures set by organizations</li>
                <li>Economic conditions and competition</li>
              </ul>
              <p className="text-charcoal/80 leading-relaxed text-sm">
                Any earnings examples or testimonials are not guarantees of income. Past performance does
                not indicate future results. You should not rely on any statements of potential earnings.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">9. Intellectual Property</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              The Service and its original content, features, and functionality are owned by Venndela and are
              protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-charcoal/80 leading-relaxed">
              You retain ownership of content you upload but grant Venndela a license to use, display, and
              distribute such content as necessary to provide the Service.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">10. Termination</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              We may terminate or suspend your account immediately, without prior notice or liability, for any
              reason, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-charcoal/80 space-y-2 mb-4">
              <li>Breach of these Terms</li>
              <li>Violation of the Acceptable Use Policy</li>
              <li>Operating a prohibited business category</li>
              <li>Fraudulent or deceptive activity</li>
              <li>Non-payment of fees</li>
            </ul>
            <p className="text-charcoal/80 leading-relaxed">
              Upon termination, your right to use the Service will immediately cease. Outstanding affiliate
              commissions may be forfeited if termination results from policy violations.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">11. Limitation of Liability</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              To the maximum extent permitted by law, Venndela shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, including but not limited to loss of profits, data,
              or goodwill, arising from your use of the Service.
            </p>
            <p className="text-charcoal/80 leading-relaxed">
              Our total liability for any claims under these Terms shall not exceed the amount you paid us in
              the twelve (12) months preceding the claim.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">12. Indemnification</h2>
            <p className="text-charcoal/80 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Venndela and its officers, directors, employees,
              and agents from any claims, damages, losses, or expenses arising from your use of the Service,
              violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">13. Dispute Resolution</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              Any disputes arising from these Terms shall be resolved through binding arbitration in accordance
              with the rules of the American Arbitration Association. The arbitration shall be conducted in
              English in the State of Delaware.
            </p>
            <p className="text-charcoal/80 leading-relaxed">
              You agree to waive any right to participate in a class action lawsuit or class-wide arbitration.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">14. Changes to Terms</h2>
            <p className="text-charcoal/80 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of material changes
              by email or through the Service. Your continued use of the Service after such modifications
              constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-charcoal mb-4">15. Contact Information</h2>
            <p className="text-charcoal/80 leading-relaxed">
              For questions about these Terms, please contact us at:
            </p>
            <ul className="text-charcoal/80 mt-4 space-y-1">
              <li>Email: <a href="mailto:legal@venndela.com" className="text-rust hover:text-rust-light">legal@venndela.com</a></li>
              <li>Support: <a href="mailto:support@venndela.com" className="text-rust hover:text-rust-light">support@venndela.com</a></li>
            </ul>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 pt-8 border-t border-tan/30">
          <h3 className="font-display text-lg font-bold text-charcoal mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/legal/privacy"
              className="text-rust hover:text-rust-light underline"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/acceptable-use"
              className="text-rust hover:text-rust-light underline"
            >
              Acceptable Use Policy
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-white/10 py-12 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-display text-xl font-bold text-warm-white">
            venndela
          </Link>
          <p className="text-charcoal/50 text-sm">
            © {new Date().getFullYear()} Venndela. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
